import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form/dist/index.ie11';
import { useMealCreditFormData } from 'src/components/oneCorp/features/MealCredit/components/Form/MealCreditDataProvider/MealCreditDataProvider';
import { useSetDefaultTimeRange } from 'src/components/oneCorp/features/MealCredit/components/Form/Rules/DurationsRule/hooks/useSetDefaultTimeRange';
import { useTimeIntervalTypeAutoUpdate } from 'src/components/oneCorp/features/MealCredit/components/Form/Rules/DurationsRule/hooks/useTimeIntervalTypeAutoUpdate';
import { calculateConsecutiveDaysBasedOnBudgetLength } from 'src/components/oneCorp/features/MealCredit/utils/calculateConsecutiveDaysBasedOnBudgetLength';
import { calculateConsecutiveMonthsBasedOnBudgetLength } from 'src/components/oneCorp/features/MealCredit/utils/calculateConsecutiveMonthsBasedOnBudgetLength';
import {
  defaultDayTags,
  IMealCreditFormFields,
} from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';

export const useManageMealCreditForm = (): void => {
  const { register, setValue } = useFormContext<IMealCreditFormFields>();

  const {
    formActions: { setFormFieldsWithMealCreditData, initForm },
    formValues: {
      budgetLength,
      budgetResetPeriod,
      budgetResetType,
      isDaysAvailable,
    },
    mealCredit,
    corpDataValues: { corpData },
  } = useMealCreditFormData();

  useEffect(() => {
    register('numberOfConsecutiveDays');
    register('numberOfConsecutiveMonths');
  }, [register]);

  useEffect(() => {
    setFormFieldsWithMealCreditData(mealCredit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mealCredit?.id]);

  useEffect(() => {
    if (!mealCredit && corpData) {
      initForm(corpData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpData, mealCredit]);

  useEffect(() => {
    if (!isDaysAvailable) {
      setValue('days', defaultDayTags, { shouldValidate: true });
    }
  }, [isDaysAvailable, setValue]);

  useEffect(() => {
    const numberOfConsecutiveDays = calculateConsecutiveDaysBasedOnBudgetLength(
      {
        budgetLength,
        budgetResetPeriod,
        budgetResetType,
      }
    );
    setValue('numberOfConsecutiveDays', numberOfConsecutiveDays);

    const numberOfConsecutiveMonths =
      calculateConsecutiveMonthsBasedOnBudgetLength({
        budgetLength,
        budgetResetPeriod,
        budgetResetType,
      });
    setValue('numberOfConsecutiveMonths', numberOfConsecutiveMonths);
  }, [budgetResetPeriod, budgetLength, setValue, budgetResetType]);

  useTimeIntervalTypeAutoUpdate();
  useSetDefaultTimeRange();
};
